/**
 * The course-programme vocabulary shared by the #3 register filters (T.Z43), the #26.2
 * cabinet filters and the partner-CSV course resolution.
 *
 * WHY IT LIVES HERE: three consumers in three packages must not drift — the api resolves
 * `?audience=` into course ids (#3) and into a `programs.audience` IN-set (#26.2), the
 * importer folds the partner's «Целевая аудитория» cell onto the same buckets, and the web
 * builds its «Аудитория» select from the same three values. A fourth copy in any of them
 * would eventually disagree about what «СПО» means.
 *
 * The dictionary's own values are NOT this enum: `programs.audience` holds display strings
 * («8-9 кл.», «10-11 кл.», «СПО», `college` — see the COURSE_INFO map of the seed), written
 * by ADMIN and transcribed from the owner's sheet. {@link audienceBucketOf} is the ONE
 * fold from such a display string onto a filter value, so a dictionary re-spelling keeps
 * answering the same filter.
 */
/** The `?audience=` vocabulary of #3 and #26.2 (v1.8.8) — the campaign's three audiences. */
export const AUDIENCE_FILTERS = ['8-9', '10-11', 'СПО'];
/** Dashes and digit separators that vary by keyboard (`8–9`, `8‑9`, `8 9`). */
const DASH_LIKE = /[‐-―−﹘－]/g;
/**
 * «Целевая аудитория» (a dictionary display string, or the partner's cell) → the filter
 * bucket it names, or `null` when the text names none («», a blank cell, an unparseable
 * caption). One fold, three rules, in this order:
 *   1. `спо` / `колледж` / `college` → `СПО` — the СПО courses are advertised both as
 *      «студентов СПО» and «студентов колледжа» (the ONLINE twins carry the English
 *      spelling in the dictionary), and they are ONE audience to the owner;
 *   2. `10-11` → `10-11`;
 *   3. `8-9` → `8-9`.
 *
 * Deliberately NOT the address normaliser (`normalizeText` drops city prefixes and folds
 * `ё` — semantics a course caption must not inherit). NFKC + lowercase + dash unification
 * is everything a bucket needs.
 */
export function audienceBucketOf(value) {
    if (value === null || value === undefined)
        return null;
    const text = value.normalize('NFKC').toLowerCase().replace(DASH_LIKE, '-').replace(/\s+/g, ' ').trim();
    if (text === '')
        return null;
    if (text.includes('спо') || text.includes('колледж') || text.includes('college'))
        return 'СПО';
    if (text.includes('10-11') || text.includes('10 11'))
        return '10-11';
    if (text.includes('8-9') || text.includes('8 9'))
        return '8-9';
    return null;
}
/**
 * A query value → `AudienceFilter`, or `null` when it is outside the vocabulary. The
 * casing is normalised (`спо` → `СПО`) exactly like #3's `format`; the digit buckets have
 * no case to normalise and pass through untouched.
 */
export function audienceFilterOf(value) {
    const text = value.normalize('NFKC').trim().toUpperCase().replace(DASH_LIKE, '-');
    return AUDIENCE_FILTERS.includes(text) ? text : null;
}
/**
 * T.Z173 — the `applications.format` axis' dictionary fallback, folded ONCE here
 * because three writers must not drift (the unti /v1 sync, the registry snapshot
 * engine's create/update/bridge paths, and the backfill migration 0042 by
 * construction).
 *
 * THE RULE (owner decision, 2026-09-23): при первом появлении заявки формат берётся
 * из словаря курсов, если выгрузка его не принесла; уже проставленный формат никогда
 * не перезаписывается. The dictionary (`programs.formats`, `course_format[]`) knows
 * the format for every campaign course — each row holds EXACTLY one literal — while
 * the exports often do not (the УНТИ-first rows carry no «Формат» cell at all; the
 * newer ЕПГУ exports stopped filling theirs). A row whose format would otherwise stay
 * NULL therefore inherits the course's own, and the ЕПГУ sheet's own verdict —
 * whenever the export actually NAMES one — stays the senior source (see 02-data-model
 * → field ownership).
 *
 * Shared stays Prisma-free: the two literals are spelled out here and must match the
 * DB enum `course_format` (schema.prisma `CourseFormat`) — a third value there is a
 * contract change that has to come back to this file.
 */
/** The literals of the DB enum `course_format` — the format axis' whole vocabulary. */
export const COURSE_FORMAT_LITERALS = ['OFFLINE', 'ONLINE'];
/**
 * The dictionary's `formats` array for ONE course → the format it pins, or `null`
 * when the course does not commit to one: an empty array, several variants, or a
 * value outside the enum all read as "no verdict". The engine never guesses — a
 * course advertised in BOTH formats cannot colour a row.
 */
export function singleCourseFormatOf(formats) {
    if (formats === null || formats === undefined || formats.length !== 1)
        return null;
    const only = formats[0];
    return only === 'OFFLINE' || only === 'ONLINE' ? only : null;
}
/**
 * The whole dictionary → a `course_id → format` index holding ONLY the courses that
 * pin exactly one literal (a both-formats course is simply absent, so a lookup reads
 * "no verdict" instead of a guess). Build it once per pass — the unti sync and the
 * snapshot apply already read the dictionary in one bulk query, and the index is the
 * same read with the fold applied.
 */
export function courseFormatIndex(programs) {
    const index = new Map();
    for (const program of programs) {
        const single = singleCourseFormatOf(program.formats);
        if (single !== null)
            index.set(program.courseId, single);
    }
    return index;
}
/**
 * The lookup: the row's `course_id` → the dictionary format, or `null` when the
 * course is unknown to the index (not in the dictionary at all, or advertising both
 * variants) or when the row has no course yet. `null` = "the fallback has nothing to
 * say" — it never means OFFLINE.
 */
export function formatFromPrograms(index, courseId) {
    if (courseId === null || courseId === undefined)
        return null;
    return index.get(courseId) ?? null;
}
//# sourceMappingURL=programs.js.map